<template>
  <ODialog
    ref="dialog"
    :maxWidth="1200"
    :transition="dialogTransition"
    hideWhenLoading
  >
    <v-card v-if="!isLoading">
      <v-toolbar dark>
        <v-toolbar-title>{{ dialogTitle }} </v-toolbar-title>
        <v-spacer />
        <v-btn
          v-if="unpauserV2Cid != null"
          :href="`/tools/unpauser/index.html?clientId=${unpauserV2Cid}`"
          target="blank"
        >
          Unpauser V2
          <v-icon>mdi-open-in-new</v-icon>
        </v-btn>
        <v-spacer />
        <v-btn icon @click="dismiss()">
          <v-icon class="ml-2">close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-tabs v-model="activeTab" showArrows background-color="grey lighten-2">
        <v-tab ripple href="#worker">Worker</v-tab>
        <v-tab ripple href="#actions">Actions</v-tab>
        <v-tab ripple href="#active">Active Head</v-tab>
        <v-tab ripple href="#waiting">Waiting Head</v-tab>
        <v-tab ripple href="#peek">Mensagens</v-tab>
        <v-tab ripple href="#json">JSON</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" touchless>
        <v-tab-item value="worker">
          <v-card-text class="pa-3" v-if="queue != null && worker != null">
            <v-row wrap class="ma-0">
              <v-col cols="12" class="pa-3">
                <label class="light-caption">Módulo</label>
                <p class="big-content">
                  <router-link
                    :to="`/orgs/${queue.orgCode}`"
                    v-html="queue.orgCode"
                  />
                  ::
                  <router-link
                    v-if="isClientId(queue.clientId)"
                    :to="`/clients/${queue.clientId}`"
                    v-html="queue.clientId"
                  />
                  <span v-else>{{ queue.clientId }}</span>
                  ::
                  {{ queue.vehicleCode || '(s/placa)' }}
                </p>
              </v-col>

              <v-col cols="12" lg="6" class="pa-3">
                <label class="light-caption">Active/Waiting</label>
                <p class="big-content">
                  {{ queue.active || 0 }} &mdash; {{ queue.waiting || 0 }}
                </p>
              </v-col>

              <v-col cols="12" lg="6" class="pa-3">
                <label class="light-caption">Estado</label>
                <v-chip
                  small
                  dark
                  label
                  text-color="white"
                  :color="workerLabelClass"
                >
                  {{ worker.state || 'missing' }}
                </v-chip>

                (há {{ worker.inStateSince | timeago('HH:mm:ss') }})
              </v-col>

              <v-col cols="12" lg="6" class="pa-3">
                <label class="light-caption">Iniciado em</label>
                {{ worker.lastStartedAt | timeAndSince }}
              </v-col>

              <v-col cols="12" lg="6" class="pa-3">
                <label class="light-caption">Conectou em</label>
                {{ worker.lastConnectedAt | timeAndSince }}
              </v-col>

              <v-col cols="12" lg="6" class="pa-3">
                <label class="light-caption">Diagnosticado em</label>
                {{ worker.diagnosedAt | timeAndSince }}
              </v-col>

              <v-col cols="12" lg="6" class="pa-3">
                <label class="light-caption">Último erro em</label>
                {{ worker.lastWorkerErrorAt | timeAndSince }}
              </v-col>

              <v-col cols="12" class="pa-3" v-if="workerErrorStack != null">
                <label class="light-caption">Último erro</label>
                <pre class="scroll-x">{{ workerErrorStack }}</pre>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

        <v-tab-item value="actions">
          <v-card-text class="pa-6">
            <p>
              Se o worker parou devido a um problema, é possível reiniciá-lo. O
              sistema pode ser instruído a ignorar alguns erros; isto só se
              aplicará ao próximo HB processado.
            </p>
            <v-row wrap class="ma-0">
              <v-col cols="12" sm="4" class="pa-3">
                <v-switch
                  v-model="extraInfo.ignoreMissingDriver"
                  label="Ignorar crachá desconhecido"
                  hint="O trecho ficará SEM MOTORISTA"
                  persistent-hint
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12" sm="4" class="pa-3">
                <v-switch
                  v-model="extraInfo.ignoreDates"
                  label="Ignorar datas inconsistentes"
                  hint="Datas no futuro, no passado distante, trechos muito longos, etc"
                  persistent-hint
                  disabled
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12" sm="4" class="pa-3">
                <v-switch
                  v-model="extraInfo.ignoreHardGap"
                  label="Ignorar hard gap"
                  hint="Fecha o trecho atual, invalidando se muito curto, e prossegue."
                  persistent-hint
                  class="mt-0"
                />
              </v-col>

              <v-col cols="12" sm="4" class="pa-3 offset-sm-4">
                <OHoldToConfirm
                  class="ma-0"
                  style="height: 75px"
                  block
                  large
                  :color="btnColor"
                  @confirmed="emitRestart"
                >
                  Restart Worker
                </OHoldToConfirm>
              </v-col>
            </v-row>

            <v-divider class="my-4" />

            <v-row wrap class="ma-0">
              <v-col cols="12" class="pa-6">
                <p>
                  É possível limpar a fila, caso você tenha certeza que todos os
                  heartbeats que ela contém podem ser descartados. Observe que
                  ainda é necessário reiniciar o worker.
                </p>
              </v-col>

              <v-col cols="12" sm="4" class="offset-sm-4">
                <OHoldToConfirm
                  class="ma-0"
                  style="height: 75px"
                  block
                  large
                  :color="btnColor"
                  @confirmed="emitFlush"
                >
                  Flush Queue
                </OHoldToConfirm>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

        <v-tab-item value="cur">
          <v-card-text>
            <pre class="scroll-x" v-if="worker != null">{{
              worker.currentMessage | prettyJson('(vazio)')
            }}</pre>
          </v-card-text>
        </v-tab-item>

        <v-tab-item value="prev">
          <v-card-text>
            <pre class="scroll-x" v-if="worker != null">{{
              worker.previousMessage | prettyJson('(vazio)')
            }}</pre>
          </v-card-text>
        </v-tab-item>

        <v-tab-item value="active">
          <v-card-text>
            <pre class="scroll-x" v-if="queue != null">{{
              queue.activeHead | prettyJson('(vazio)')
            }}</pre>
          </v-card-text>
        </v-tab-item>

        <v-tab-item value="waiting">
          <v-card-text>
            <pre class="scroll-x" v-if="queue != null">{{
              queue.waitingHead | prettyJson('(vazio)')
            }}</pre>
          </v-card-text>
        </v-tab-item>

        <v-tab-item value="peek">
          <v-card-text>
            <v-expansion-panels
              accordion
              multiple
              focusable
              hover
              tile
              class="mb-4"
            >
              <v-expansion-panel v-for="(msg, i) in msgs" :key="i">
                <v-expansion-panel-header>
                  Mensagem #{{ i }}
                  <span v-if="i == 0" class="ml-1">(próxima na fila)</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="noPaddingExpansionPanel">
                  <pre class="scroll-x">{{ msg | prettyJson }}</pre>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-btn @click="fetchMoreMessages">Buscar mais 5</v-btn>
            <v-btn @click="resetMessages">Recomeçar</v-btn>
          </v-card-text>
        </v-tab-item>

        <v-tab-item value="json">
          <v-card-text>
            <pre class="scroll-x">{{ worker | prettyJson('(vazio)') }}</pre>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </ODialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { Queue, PipeDiags } from '@/models'
import { timeago } from 'oderon-commons/src/formatters'

export default {
  mixins: [require('oderon-vue/cpts/dialog-promise-mixin').default],
  components: {
    OHoldToConfirm: require('oderon-vue/cpts/OHoldToConfirm').default,
    ODialog: require('oderon-vue/cpts/ODialog').default
  },
  data() {
    return this.reset()
  },
  computed: {
    unpauserV2Cid() {
      let cid = this.queue && this.queue.clientId
      cid = +cid

      if (Number.isFinite(cid)) return cid
      return null
    },

    dialogTransition() {
      if (this.$isMobile) return 'dialog-bottom-transition'
      else return null
    },

    workerErrorStack() {
      let stack = _.get(this, 'worker.lastWorkerErrorStack')
      if (stack != null) return stack

      let err = _.get(this, 'worker.lastWorkerError')
      if (err == null) return null

      let result = err.stack
      if (err.cause && err.cause.stack != err.stack)
        result += '\ncause: ' + err.cause.stack

      return result
    },

    workerLabelClass() {
      let state = _.get(this, 'worker.state')
      if (state == null) return 'grey'
      if (state == 'active') return 'info'
      if (state == 'waiting') return 'success'
      return 'error'
    },

    btnColor() {
      let name = this.qname
      if (name == null) return 'error'
      if (name.includes('dev/')) return 'success'
      if (name.includes('hlg/')) return 'amber darken-1 white--text'
      return 'error'
    },

    dialogTitle() {
      let r = this.qname
      if (this.queue != null && this.queue.vehicleCode != null) {
        r = `${this.qname} — ${this.queue.vehicleCode}`
      }
      return r
    }
  },
  methods: {
    reset() {
      return {
        isLoading: false,
        activeTab: 'worker',
        qname: null,

        queue: null,
        worker: null,

        // usado no restart-worker
        extraInfo: {},

        // preview das msgs na fila
        msgs: [],
        msgOffset: 0
      }
    },

    async open(qname) {
      Object.assign(this, this.reset())
      this.qname = qname
      this.$refs.dialog.$loading(() => this.fetchData())
      return this.$refs.dialog.$open()
    },

    dismiss() {
      return this.$refs.dialog.$dismiss()
    },

    async fetchData() {
      // await this.$delay(2000)
      let qname = this.qname

      this.queue = await Queue.findById(qname)

      // Se queue.worker não está null, é o formato
      // novo (qman/Consumer2). Caso esteja null, pode
      // ser buscado no PipeDiags, EXCETO se for uma
      // fila como org/commands, tapman, etc.
      if (this.queue.worker != null) {
        this.worker = this.queue.worker
      } else if (qname.match(/[-\w]+\/\d+$/)) {
        this.worker = await PipeDiags.findById(qname)
      } else {
        this.worker = {}
      }
    },

    async fetchMoreMessages() {
      let newMsgs = await Queue.peek(this.qname, {
        offset: this.msgOffset
      })
      this.msgs.push(...newMsgs)
      this.msgOffset += newMsgs.length
    },

    async resetMessages() {
      this.msgOffset = 0
      this.msgs = []
      await this.fetchMoreMessages()
    },

    emitFlush() {
      this.$refs.dialog.$apply('FLUSH')
    },

    emitRestart() {
      this.$refs.dialog.$apply({
        orgCode: this.queue.orgCode,
        clientId: this.queue.clientId,
        extraInfo: _.clone(this.extraInfo)
      })
    },

    isClientId(x) {
      return x != null && x.toString().match(/^\d+$/)
    }
  },
  filters: {
    timeAndSince(x) {
      if (x == null) return '-'
      let m = moment(x).format(),
        a = timeago(x)
      return `${m} (há ${a})`
    }
  }
}
</script>

<style>
.scroll-x {
  overflow-x: auto;
  white-space: pre-wrap;
}
</style>
